var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useEffect } from 'react';
import { describeArc } from '../../clock/helper';
// -------------------------------------------------------------------------
// CONSTANTS for the SVG circle
var SVG_SIZE = 300;
var CENTER = SVG_SIZE / 2;
var RADIUS = SVG_SIZE / 2;
var BORDER_COLOR = '#293273';
var BORDER_WIDTH = 1.5;
/**
 * CirclePartitionSVG: Handles internal slice colors but responds to props for partitioning and selected color.
 * @param {object} props
 * @param {string} props.initialColor - The default color for all slices on initialization.
 * @param {string} props.selectedColor - The color to apply when a slice is clicked.
 * @param {number} props.numberofpartition - The current number of slices required.
 */
function CirclePartitionSVG(_a) {
    var initialColor = _a.initialColor, selectedColor = _a.selectedColor, numberofpartition = _a.numberofpartition, _b = _a.disabled, disabled = _b === void 0 ? false : _b, storedColors = _a.storedColors, onColorsChange = _a.onColorsChange, _c = _a.focusedIndex, focusedIndex = _c === void 0 ? null : _c;
    // Use stored colors if provided, otherwise use internal state
    var _d = useState([]), sliceColors = _d[0], setSliceColors = _d[1];
    var isControlled = storedColors !== undefined;
    // --- EFFECT: Initialize colors from storedColors or default ---
    useEffect(function () {
        var N = Math.max(1, numberofpartition); // Ensure N >= 1
        if (isControlled) {
            // If controlled, ensure colors array matches partition count
            if (storedColors === undefined || storedColors.length === 0) {
                // Initialize with transparent colors if no stored colors
                if (onColorsChange) {
                    onColorsChange(Array(N).fill(initialColor || 'transparent'));
                }
            }
            else if (storedColors.length !== N && onColorsChange) {
                // Adjust array size to match partition count
                var newColors = __spreadArray([], storedColors, true);
                // Pad with initialColor if needed
                while (newColors.length < N) {
                    newColors.push(initialColor || 'transparent');
                }
                // Trim if needed
                if (newColors.length > N) {
                    newColors.splice(N);
                }
                // Update parent if colors changed
                onColorsChange(newColors);
            }
        }
        else {
            // Initialize internal state if not controlled
            if (sliceColors.length !== N) {
                setSliceColors(Array(N).fill(initialColor || 'transparent'));
            }
        }
    }, [numberofpartition, initialColor, isControlled, storedColors, onColorsChange]);
    // Use stored colors if controlled, otherwise use internal state
    var currentColors = useMemo(function () {
        if (isControlled) {
            var N = Math.max(1, numberofpartition);
            if (storedColors && storedColors.length === N) {
                return storedColors;
            }
            // Return array of initialColor if no stored colors yet or size mismatch
            return Array(N).fill(initialColor || 'transparent');
        }
        return sliceColors;
    }, [isControlled, storedColors, sliceColors, numberofpartition, initialColor]);
    var partitionCount = currentColors.length;
    var sliceAngle = partitionCount > 0 ? 360 / partitionCount : 0;
    // --- Handler for Coloring a Slice on Click (Toggle behavior) ---
    var handleSliceClick = useCallback(function (index) {
        if (disabled)
            return;
        var newColors = __spreadArray([], currentColors, true);
        var currentColor = newColors[index];
        // Toggle: if already colored with selectedColor, remove it (set to initialColor)
        if (currentColor === selectedColor) {
            newColors[index] = initialColor || 'transparent';
        }
        else {
            // Otherwise, apply the selectedColor
            newColors[index] = selectedColor;
        }
        // Update colors based on whether component is controlled or not
        if (isControlled && onColorsChange) {
            onColorsChange(newColors);
        }
        else {
            setSliceColors(newColors);
        }
    }, [selectedColor, initialColor, disabled, currentColors, isControlled, onColorsChange]);
    // --- Memoize Path Data and Rendering ---
    var svgPaths = useMemo(function () {
        var currentAngle = 0;
        return currentColors.map(function (color, index) {
            var startAngle = currentAngle;
            var endAngle = currentAngle + sliceAngle;
            var pathData = describeArc(CENTER, CENTER, RADIUS, startAngle, endAngle);
            currentAngle = endAngle;
            var isFocused = focusedIndex === index;
            return (_jsx("path", { d: pathData, fill: color, fillOpacity: color && color !== 'transparent' ? 0.5 : 1, stroke: isFocused ? '#E16B00' : BORDER_COLOR, strokeWidth: isFocused ? 3 : BORDER_WIDTH, onClick: function () { return handleSliceClick(index); }, className: "chart-slice" }, index));
        });
    }, [currentColors, sliceAngle, handleSliceClick, focusedIndex]);
    return (_jsx("div", { className: "container-svg", children: _jsx("svg", { width: SVG_SIZE, height: SVG_SIZE, viewBox: "0 0 ".concat(SVG_SIZE, " ").concat(SVG_SIZE), className: "partitioned-circle-svg", children: svgPaths }) }));
}
export default CirclePartitionSVG;
